import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, useTheme, SxProps, Theme } from '@mui/material';

export interface ButtonProps extends Omit<MuiButtonProps, 'size' | 'sx'> {
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
}

const ButtonMAIQ: React.FC<ButtonProps> = ({ 
  variant = 'contained',
  color = 'secondary',
  size = 'medium',
  sx,
  children,
  ...props 
}) => {
  const theme = useTheme();

  // Default styles based on variant and color
  const getDefaultStyles = (): SxProps<Theme> => {
    const baseStyles: SxProps<Theme> = {
      textTransform: 'none',
      borderRadius: 1,
      fontWeight: 600,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    };

    if (variant === 'contained') {
      if (color === 'secondary') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.secondary.main,
          boxShadow: theme.shadows[1],
          color: theme.palette.secondary.contrastText || 'white',
          py: size === 'small' ? 0.5 : size === 'large' ? 1.25 : 0.75,
          px: size === 'small' ? 1.5 : size === 'large' ? 4 : 2.5,
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark || '#0a1f3a',
            boxShadow: theme.shadows[1],
          },
          '&:disabled': {
            backgroundColor: 'rgba(15, 40, 76, 0.3)',
            color: 'rgba(255, 255, 255, 0.5)',
          },
        };
      } else if (color === 'primary') {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText || 'white',
          boxShadow: theme.shadows[1],
          py: size === 'small' ? 0.5 : size === 'large' ? 1.25 : 0.75,
          px: size === 'small' ? 1.5 : size === 'large' ? 4 : 2.5,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: theme.shadows[1],
          },
        };
      }
    } else if (variant === 'outlined') {
      return {
        ...baseStyles,
        color: 'text.secondary',
        borderColor: 'divider',
        py: size === 'small' ? 0.5 : size === 'large' ? 1.25 : 0.75,
        px: size === 'small' ? 1.5 : size === 'large' ? 4 : 2.5,
        '&:hover': {
          borderColor: 'divider',
          backgroundColor: theme.palette.action.hover,
        },
      };
    } else if (variant === 'text') {
      return {
        ...baseStyles,
        color: 'text.secondary',
        py: size === 'small' ? 0.5 : size === 'large' ? 1.25 : 0.75,
        px: size === 'small' ? 1 : size === 'large' ? 3 : 2,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      };
    }

    return baseStyles;
  };

  const defaultStyles = getDefaultStyles();
  const mergedSx: SxProps<Theme> = sx ? [defaultStyles, sx] as SxProps<Theme> : defaultStyles;

  return (
    <MuiButton
      variant={variant}
      color={color}
      size={size}
      sx={mergedSx}
      {...props}
    >
      {children}
    </MuiButton>
  );
};

export default ButtonMAIQ;

